/**
 * Qual idioma o app fala, decidido pelo que a pessoa comprou.
 *
 * Os catálogos não são traduções um do outro: Clean Sweets e Repostería Cero
 * são acervos diferentes, escritos em idiomas diferentes. Mostrar a interface
 * num idioma e o conteúdo em outro nunca serviu a ninguém — quem comprou um
 * produto inglês lê receitas em inglês, e a interface acompanha.
 *
 * Por isso não há seletor dentro do app. A exceção são as ~35 contas com
 * produtos de mais de um idioma: para elas a escolha existe, limitada aos
 * idiomas que elas de fato possuem.
 */
import type { SupportedLanguage } from "@/i18n";

const EN_PRODUCT_IDS = new Set([
  "7456362", // Clean Bakes (legado)
  "8061842", // Clean Bakes
  "7381203", // Clean Sweets
  "7381661", // Clean Meals
  "8073539", // Clean Remedies
  "8495830", // Clean Cakes
  "8597307", // Clean Mornings
]);

const DE_PRODUCT_IDS = new Set([
  "7778364", // Küchenglück
  "7778710", // Brotglück
  "7778879", // Kochglück
]);

const ES_PRODUCT_IDS = new Set([
  "7222660", "7418452", // Panadería Cero
  "7368845", "7224807", // Repostería Cero (7224807 era o PDF Sin TACC)
  "7339562", "7368977", // Cocina Real
  "8131891",            // La Botica de la Abuela
]);

/**
 * Os idiomas que os produtos da pessoa cobrem.
 *
 * Produto que não pertence a nenhum acervo — assinatura do Glucofy AI,
 * GlucoPhoto, Natasha Premium — não entra: ele não traz catálogo e portanto
 * não tem idioma a impor.
 */
export function productLanguages(productIds: string[]): SupportedLanguage[] {
  const idiomas = new Set<SupportedLanguage>();
  for (const id of productIds) {
    if (EN_PRODUCT_IDS.has(id)) idiomas.add("en");
    else if (DE_PRODUCT_IDS.has(id)) idiomas.add("de");
    else if (ES_PRODUCT_IDS.has(id)) idiomas.add("es");
  }
  // Ordem estável, e a mesma prioridade de sempre: EN antes de DE antes de ES.
  return (["en", "de", "es"] as const).filter((l) => idiomas.has(l));
}

/**
 * O idioma a impor, ou null quando não há o que impor — porque a pessoa tem
 * produtos de mais de um idioma, ou não tem produto com catálogo nenhum.
 */
export function enforcedLanguage(productIds: string[]): SupportedLanguage | null {
  const idiomas = productLanguages(productIds);
  return idiomas.length === 1 ? idiomas[0] : null;
}

/**
 * Se a pessoa pode escolher o idioma dentro do app. Só quem tem catálogo em
 * mais de um idioma — as ~35 contas multi-mercado.
 */
export function canChooseLanguage(productIds: string[]): boolean {
  return productLanguages(productIds).length > 1;
}
